import { z } from 'zod';

export type ActivationCondition =
  | { kind: 'always' }
  | { kind: 'workspaceContains'; glob: string }
  | { kind: 'serverCapability'; capability: string };

export const conditionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('always') }),
  z.object({ kind: z.literal('workspaceContains'), glob: z.string().min(1) }),
  z.object({ kind: z.literal('serverCapability'), capability: z.string().min(1) }),
]);

export type WorkbenchPanelContribution = {
  id: string;
  title: string;
  summaryLabel?: string;
  /** What it is for, shown when the launcher offers it. */
  description?: string | undefined;
  /** A glyph name for a surface of the core; an app is shown by its own icon (`display.icon`). */
  icon?: string;
  /** A surface the core renders itself (the browser, terminals, the live call). */
  hostView?:
    | 'terminal-surface'
    | 'web-surface'
    | 'live'
    /** A project conversation's Tasks and Project tabs (D65, D67). */
    | 'project-tasks'
    | 'project';
  /** A view of a Crowdex app (`crowdex.json`), on the plugin's own origin. */
  appView?: {
    plugin: string;
    path: string;
    media?: ('camera' | 'microphone')[];
  };
  /** The remote web app of a plugin (`page.url`), its origin resolved by the server. */
  remoteView?: { plugin: string };
  /**
   * An app (`page` in `crowdex.json`): outside a conversation it opens its home, in one it docks
   * as a tab of the side panel, its `sidebars` sections in the sidebar while that tab is shown.
   */
  rail?: { sidebars?: readonly { id: string; title: string; route: string }[] | undefined };
  when?: ActivationCondition | undefined;
  session?: WorkbenchPanelSessionContribution | undefined;
};

export type WorkbenchPanelSessionContribution = {
  surface: 'web' | 'terminal';
  multiple: true;
  shortcut?:
    | {
        key: string;
        commandOrControl: boolean;
        shift?: boolean | undefined;
        alt?: boolean | undefined;
      }
    | undefined;
  newInstanceLabel: string;
  mentionKind: 'tab' | 'terminal';
  mentionDetail: string;
};

/** A concrete ACP implementation contributed by an independently packaged plugin. */
export const acpAgentContributionSchema = z
  .object({
    id: z.string().regex(/^[a-zA-Z0-9._-]{1,128}$/),
    localId: z.string().regex(/^auto-[a-zA-Z0-9._-]+$/),
    name: z.string().min(1).max(160),
    family: z
      .string()
      .regex(/^[a-z0-9][a-z0-9._-]*$/)
      .nullable()
      .default(null),
    /** Offered by default when this producer is installed. */
    default: z.boolean().default(false),
    discovery: z
      .object({
        binaries: z.array(z.string().min(1).max(256)).min(1).max(32),
      })
      .strict(),
    launch: z
      .object({
        args: z.array(z.string().max(4096)).max(64).default([]),
        npm: z
          .object({
            package: z.string().min(1).max(214),
            bin: z.string().min(1).max(256).optional(),
          })
          .strict()
          .optional(),
        env: z.record(z.string().regex(/^[A-Z][A-Z0-9_]*$/), z.string()).optional(),
      })
      .strict(),
    aliases: z.array(z.string().min(1).max(128)).max(32).default([]),
    model: z
      .discriminatedUnion('target', [
        z.object({ target: z.literal('session') }).strict(),
        z
          .object({
            target: z.literal('env'),
            name: z.string().regex(/^[A-Z][A-Z0-9_]*$/),
          })
          .strict(),
      ])
      .optional(),
    sessionConfig: z
      .array(
        z
          .object({
            aliases: z.array(z.string().min(1).max(128)).min(1).max(32),
            target: z.string().min(1).max(128),
            choices: z.array(z.string().min(1).max(128)).max(32).optional(),
          })
          .strict(),
      )
      .max(32)
      .default([]),
    modelCompatibility: z
      .object({
        includePrefixes: z.array(z.string().min(1).max(128)).max(16).optional(),
        excludePrefixes: z.array(z.string().min(1).max(128)).max(16).optional(),
      })
      .strict()
      .optional(),
    providerInjection: z
      .object({
        baseUrl: z.discriminatedUnion('target', [
          z
            .object({
              target: z.literal('env'),
              name: z.string().regex(/^[A-Z][A-Z0-9_]*$/),
              appendPath: z
                .string()
                .regex(/^\/[A-Za-z0-9._~!$&'()*+,;=:@%/-]*$/)
                .optional(),
            })
            .strict(),
          z
            .object({
              target: z.literal('argument'),
              name: z.string().min(1).max(128),
              appendPath: z
                .string()
                .regex(/^\/[A-Za-z0-9._~!$&'()*+,;=:@%/-]*$/)
                .optional(),
            })
            .strict(),
        ]),
        auth: z
          .object({
            target: z.literal('env'),
            name: z.string().regex(/^[A-Z][A-Z0-9_]*$/),
          })
          .strict(),
        /**
         * Set with the credentials: what tells the agent to sign in with them. `{baseUrl}` and
         * `{token}` stand for them, escaped as in a JSON string.
         */
        env: z.record(z.string().regex(/^[A-Z][A-Z0-9_]*$/), z.string().max(4096)).optional(),
      })
      .strict()
      .optional(),
    capabilities: z
      .array(z.string().regex(/^[a-z][a-z0-9.-]+$/))
      .max(64)
      .default([]),
  })
  .strict();
export type AcpAgentContribution = z.infer<typeof acpAgentContributionSchema>;

export function contributionActive(
  condition: ActivationCondition | undefined,
  context: {
    capabilities: ReadonlySet<string>;
    workspaceMatches: ReadonlySet<string>;
  },
): boolean {
  if (!condition || condition.kind === 'always') return true;
  if (condition.kind === 'serverCapability') return context.capabilities.has(condition.capability);
  return context.workspaceMatches.has(condition.glob);
}
